/**
 * Bezorging gaat sinds oktober 2026 dinsdag t/m zaterdag via SooCool en op
 * maandag via Trunkrs (zie MAANDAG hieronder). Het eigen gekoelde transport in
 * West-Friesland is gestopt, dus regionaal en landelijk zijn een geheel
 * geworden: een tarief, een drempel, een minimum en dezelfde bezorgdagen voor
 * elk adres en elk land.
 *
 * De postcodelijsten hieronder blijven bestaan, maar hebben nog maar EEN doel:
 * het `landelijk`-vlaggetje op de order bepalen. De `api` en `fulfilment-cms`
 * lezen dat veld, dus het wordt nog steeds geschreven. Het mag alleen niets meer
 * zeggen over de prijs, de bezorgdagen of de teksten in de checkout.
 */

export const ZIPCODES_ENKHUIZEN = [1600, 1601, 1602];
export const ZIPCODES_WADDENEILANDEN = [
  1790, 1791, 1792, 1793, 1794, 1795, 1796, 1797, 8880, 8881, 8882, 8883, 8884,
  8885, 8886, 8887, 8888, 8889, 8890, 8891, 8892, 8893, 8894, 8895, 8896, 8897,
  8899, 9161, 9162, 9163, 9164, 9166,
];
export const ZIPCODES_STEDE_BROEC = [1610, 1611, 1613, 1614];
export const ZIPCODES_WEST_FRIESLAND = [
  1606, 1616, 1607, 1617, 1608, 1696, 1609, 1697, 1620, 1624, 1689, 1621, 1625,
  1695, 1622, 1627, 1623, 1628, 1631, 1642, 1648, 1633, 1643, 1652, 1634, 1711,
  1641, 1647, 1713, 1619, 1658, 1676, 1682, 1686, 1692, 1654, 1670, 1678, 1683,
  1687, 1693, 1655, 1671, 1679, 1684, 1688, 1657, 1674, 1681, 1685, 1691, 1661,
  1716, 1662, 1718, 1663, 1719, 1715,
];

/**
 * Het bezorgtarief onder de gratis-drempel. Een bedrag, ongeacht postcode of
 * land - Belgie betaalt sinds SooCool precies hetzelfde als Nederland.
 *
 * Vervangt de vier oude staffels: Enkhuizen/Stede Broec 3,95, overig
 * West-Friesland 4,95, landelijk 8,95 met een tussenstap van 5,95, en de aparte
 * zaterdagstaffel van 9,99 / 4,99.
 */
export const SEND_COST = 9.95;

/** Vanaf dit orderbedrag is bezorgen gratis. Voorheen 50 regionaal / 150 landelijk. */
export const FREE_DELIVERY_FROM_PRICE = 150;

/**
 * Het minimale orderbedrag, in euro's, gemeten over het PRODUCTsubtotaal -
 * cadeaubonnen tellen niet mee, want daar wordt niets voor bezorgd of gesneden.
 *
 * Dit is bewust een constante en niet drie: tot september 2026 bestonden
 * `MINIMUM_ORDER_AMOUNT_LANDELIJK`, `REGIONAL_DELIVERY_MIN_ORDER_AMOUNT` en
 * `SATURDAY_PICKUP_MIN_ORDER_AMOUNT` naast elkaar, alle drie op 50 maar gemeten
 * over verschillende bedragen.
 */
export const MINIMUM_ORDER_AMOUNT = 50;

/**
 * Tot hoe laat een bestelling nog meegaat met de ophaalronde van dezelfde dag.
 * SooCool haalt maandag t/m vrijdag NA 15:00 uur op (artikel 5 van de
 * overeenkomst: reguliere pickup bij Het Vleescentrum, EUR 0,00, na 15:00 uur op
 * de overeengekomen dagen). 12:00 is de marge die de slagerij nodig heeft om te
 * snijden en in te pakken; die cut-off verandert dus niet mee.
 */
export const ORDER_CUTOFF_HOUR = 12;

/**
 * De cut-off hangt aan de Nederlandse klok, niet aan die van de bezoeker. Een
 * browser in Londen staat er anders een uur naast, precies rond 12:00.
 */
export const DELIVERY_TIME_ZONE = "Europe/Amsterdam";

export const CURRENCY = "€ ";
export const CURRENCY_CODE = "EUR";
export const DELIVERY_OPTIONS = {
  PICKUP: "pickup",
  SEND: "send",
};

export const ADDRESS_TYPES = {
  DELIVERY: "DELIVERY",
  INVOICE: "INVOICE",
};

export const PICKUP_TIME_FROM = "14:00";
export const PICKUP_TIME_UNTIL = "17:00";
export const PICKUP_TIME_UNTIL_SATURDAY = "15:00";

export const DELIVERY_DATE_WARNING_DAYS = 28;

/* -------------------------------------------------------------------------- *
 * BELGIE - waar SooCool wel en niet komt
 * -------------------------------------------------------------------------- */

/**
 * De Belgische postcodegebieden die SooCool bedient.
 *
 * Artikel 5 van de overeenkomst met SooCool: in Belgie rijdt SooCool
 * UITSLUITEND de postcodegebieden 1000 t/m 1299, 1500 t/m 3999 en 8000 t/m 9999
 * - Brussel en Vlaanderen - en daar uitsluitend in het dagvenster 08:00-18:00.
 * Alles daarbuiten bezorgt SooCool niet; dat zijn onder meer 1300-1499
 * (Waals-Brabant) en 4000-7999 (Luik, Namen, Henegouwen, Luxemburg).
 *
 * Dit is een BEZORGgrens, geen prijsgrens. De toeslag van EUR 2,50 per stop die
 * hetzelfde artikel voor Belgie noemt is een INKOOPprijs die SooCool aan Het
 * Vleescentrum factureert; de klant betaalt in Belgie hetzelfde als in
 * Nederland (`SEND_COST`, gratis vanaf `FREE_DELIVERY_FROM_PRICE`) - zie
 * besluit 10 in `planning/soocool-koppeling.md`.
 *
 * Belgische postcodes zijn vier cijfers. Beide grenzen zijn inclusief.
 */
export const BE_DELIVERY_ZIP_RANGES = [
  { from: 1000, to: 1299 },
  { from: 1500, to: 3999 },
  { from: 8000, to: 9999 },
];

/* -------------------------------------------------------------------------- *
 * LEVERVENSTERS - de vijf tijdvakken uit artikel 5 van de SooCool-overeenkomst
 * -------------------------------------------------------------------------- */

/**
 * De vijf vensters die SooCool rijdt, en de enige vijf waar de klant uit kan
 * kiezen. Dit is een GESLOTEN set: artikel 5 van de overeenkomst noemt deze vijf
 * en verder niets, en `api` zet het gekozen venster rechtstreeks om in het
 * `timeWindow` van de SooCool-order. Een zesde venster verzinnen betekent een
 * order die SooCool weigert.
 *
 * Per venster:
 *
 * - `label` - de tekst zoals hij in het contract staat. DIT gaat als
 *   `delivery.timeSlot` naar Firestore en zo naar `api`; de klantvriendelijke
 *   naam blijft in de webshop. Daarom ASCII: een koppelteken, geen en-dash, en
 *   geen spaties eromheen, zodat een string-vergelijking server-side niet op
 *   onzichtbare tekens stukloopt.
 * - `nameKey` - vertaalsleutel voor de naam die de klant leest.
 * - `surcharge` - of dit venster de toeslag van `NARROW_TIMESLOT_SURCHARGE`
 *   kost. Waar bij SooCool EUR 15,00 per stop staat in plaats van EUR 14,00.
 * - `belgium` - of het venster in Belgie te kiezen is. SooCool rijdt daar
 *   UITSLUITEND het dagvenster 08:00-18:00 (zie `BE_DELIVERY_ZIP_RANGES`).
 * - `saturday` - of het venster op zaterdag te kiezen is. Het avondvenster
 *   17:00-22:00 rijdt SooCool op zaterdag niet.
 * - `monday` - of het venster op maandag te kiezen is. Maandag rijdt niet
 *   SooCool maar Trunkrs, en Trunkrs bezorgt alleen 's avonds - zie MAANDAG
 *   hieronder. Op maandag gelden de twee SooCool-vlaggen hierboven dus niet.
 *
 * De volgorde is de volgorde waarin de klant ze ziet: eerst de twee brede
 * vensters zonder toeslag, dan de twee smalle met toeslag, dan de avond.
 */
export const DELIVERY_TIME_SLOTS = [
  {
    label: "08:00-18:00",
    nameKey: "delivery_timeslot.day",
    startTime: "08:00",
    endTime: "18:00",
    surcharge: false,
    belgium: true,
    saturday: true,
    monday: false,
  },
  {
    label: "08:00-22:00",
    nameKey: "delivery_timeslot.day_extended",
    startTime: "08:00",
    endTime: "22:00",
    surcharge: false,
    belgium: false,
    saturday: true,
    monday: false,
  },
  {
    label: "08:00-12:00",
    nameKey: "delivery_timeslot.morning",
    startTime: "08:00",
    endTime: "12:00",
    surcharge: true,
    belgium: false,
    saturday: true,
    monday: false,
  },
  {
    label: "12:00-17:00",
    nameKey: "delivery_timeslot.afternoon",
    startTime: "12:00",
    endTime: "17:00",
    surcharge: true,
    belgium: false,
    saturday: true,
    monday: false,
  },
  {
    label: "17:00-22:00",
    nameKey: "delivery_timeslot.evening",
    startTime: "17:00",
    endTime: "22:00",
    surcharge: false,
    belgium: false,
    saturday: false,
    monday: true,
  },
];

/**
 * Het venster waar de checkout mee begint, en waar hij op terugvalt als de
 * klant naar een datum of een land wisselt waarop zijn keuze niet kan.
 *
 * 08:00-18:00 omdat het als enige op elke SooCool-dag kan: dinsdag t/m zaterdag,
 * Nederland en Belgie, en zonder toeslag. Een standaard die nooit stilzwijgend
 * geld kost. Alleen op maandag valt hij weg - dan is de avond het enige venster,
 * en daar landt `resolveDeliveryTimeSlot` vanzelf op.
 */
export const DEFAULT_DELIVERY_TIME_SLOT = DELIVERY_TIME_SLOTS[0].label;

/* -------------------------------------------------------------------------- *
 * MAANDAG - de Trunkrs-dag
 * -------------------------------------------------------------------------- */

/**
 * Maandag gaat NIET via SooCool maar via Trunkrs (besluit 2026-10-07). SooCool
 * haalt maandagorders al op de vrijdag ervoor op, en het lopende Trunkrs-contract
 * vraagt 50 orders per week tot het afloopt. Een vervoerder per dag, zodat het
 * magazijn niets hoeft te tellen of te verdelen:
 *
 * - Alleen het avondvenster 17:00-22:00 (de `monday`-vlag hierboven): Trunkrs
 *   bezorgt 's avonds, in Nederland en in Belgie - zo deed het dat ook tot de
 *   overstap. De SooCool-regels voor Belgie en zaterdag gelden op maandag niet.
 * - Bestellen kan tot en met zondag 23:59. De SooCool-ophaalregel van
 *   `earliestSendOffset` geldt niet: Trunkrs haalt op maandag zelf op.
 * - Maximaal 50 bezorgorders. Die daglimiet zet `api` op elke maandag
 *   (`MondayCapacityService`) en is per maandag aan te passen in de CMS; is hij
 *   bereikt, dan grijst de gewone daglimiet in `getDisabledDatesForDatePicker`
 *   maandag uit en kiest de klant dinsdag t/m zaterdag. Er schuift dus nooit een
 *   maandagorder door naar SooCool.
 * - Hetzelfde tarief als elke andere dag, en geen toeslag: het avondvenster heeft
 *   er geen.
 *
 * `api` weigert maandagorders voor SooCool (`soocool.excluded-delivery-days`),
 * zodat die regel ook in het magazijn niet van iemands geheugen afhangt.
 *
 * Afgeleid van de `monday`-vlag, zodat de tekst in de checkout en de keuze die
 * kan nooit uit elkaar lopen.
 */
export const MONDAY_DELIVERY_TIME_SLOT = DELIVERY_TIME_SLOTS.find(
  (slot) => slot.monday,
);

/**
 * De toeslag, in euro's INCLUSIEF btw, voor een van de twee smalle vensters
 * (08:00-12:00 en 12:00-17:00).
 *
 * Waarom 1,25: SooCool factureert voor die twee vensters EUR 15,00 per stop in
 * plaats van EUR 14,00 - een verschil van EUR 1,00 exclusief btw. De klant
 * betaalt inclusief btw, en bezorgkosten volgen het hoge tarief van 21%:
 * 1,00 x 1,21 = 1,21, afgerond naar boven op een kwartje EUR 1,25. Het dekt het
 * verschil dus net, en geen cent meer dan nodig.
 *
 * Een constante, zodat een nieuw contracttarief een wijziging van EEN regel is.
 * De toeslag komt BOVENOP het bezorgtarief en vervalt NIET boven de
 * gratis-drempel: hij hoort bij het gekozen venster, niet bij het orderbedrag -
 * zie `calculateShippingCost`.
 */
export const NARROW_TIMESLOT_SURCHARGE = 1.25;

/**
 * Het vensterobject bij een contractlabel, of `null` als het label niet in de
 * gesloten set zit.
 *
 * Bewust `null` en niet een stilzwijgende terugval op het dagvenster: een label
 * dat we niet kennen is een fout die zichtbaar moet blijven, niet een order die
 * per ongeluk in een ander tijdvak bezorgd wordt.
 */
export const findDeliveryTimeSlot = (label) =>
  DELIVERY_TIME_SLOTS.find((slot) => slot.label === label) || null;

/**
 * De toeslag in euro's die bij een venster hoort. 0 voor een breed venster, en
 * ook 0 voor een onbekend of ontbrekend label - de klant nooit iets rekenen voor
 * een venster dat we niet herkennen.
 */
export const getDeliveryTimeSlotSurcharge = (label) =>
  findDeliveryTimeSlot(label)?.surcharge ? NARROW_TIMESLOT_SURCHARGE : 0;
